import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/PurchaseAPI/oldStockIssueAPI';

const getErrorMessage = (err, fallback) =>
    err?.ErrorDetails?.ErrorMessage || err?.Message || err?.message || fallback;

// Mirrors legacy's OldItemsIssueTempDetails/ItemsTransferCSK dep-percent list builder:
// steps of 10% up to the configured max, with a "FullValue" (100) sentinel once the max
// reaches 100%. Direction flips depending on whether the row's own CC IS the central store.
const buildDepPercentList = (maxPercent, centralStoreCC, rowCentralCC) => {
    if (!(maxPercent > 0)) return [];
    const no = Math.floor(maxPercent / 10);
    const list = [];
    if (rowCentralCC === centralStoreCC) {
        if (no === 10) {
            for (let i = 1; i < no; i++) list.push({ Value: i * 10, Name: String(i * 10) });
            list.push({ Value: 100, Name: 'FullValue' });
        } else {
            for (let i = 1; i <= no; i++) list.push({ Value: i * 10, Name: String(i * 10) });
        }
    } else {
        const max = 10;
        if (no === 10) {
            list.push({ Value: 100, Name: 'FullValue' });
        } else {
            for (let i = no; i <= max; i++) list.push({ Value: i * 10, Name: String(i * 10) });
        }
    }
    return list;
};

// ── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchOldStockCCList = createAsyncThunk(
    'oldStockIssue/fetchOldStockCCList',
    async ({ uid, rid }, { rejectWithValue }) => {
        try {
            const res = await api.getOldStockIssueCCs(uid, rid);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load cost centers'));
        }
    }
);

export const fetchOldStockIndentList = createAsyncThunk(
    'oldStockIssue/fetchOldStockIndentList',
    async ({ ccode }, { rejectWithValue }) => {
        try {
            const res = await api.getOldIssueIndentsByCC(ccode);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load indents'));
        }
    }
);

export const fetchIndentType = createAsyncThunk(
    'oldStockIssue/fetchIndentType',
    async ({ indentno }, { rejectWithValue }) => {
        try {
            const res = await api.getIndentType(indentno);
            return res?.Data?.[0]?.CheckIndentType || '';
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load indent type'));
        }
    }
);

// Loads the issue grid, then probes the depreciation-percent ceiling for the row's own
// central CC — same two-step flow as legacy's OldItemsIssueTempDetails action.
export const fetchOldStockGrid = createAsyncThunk(
    'oldStockIssue/fetchOldStockGrid',
    async ({ ccode, indent }, { rejectWithValue }) => {
        try {
            const res = await api.getOldItemsIssueTempDetails(ccode, indent);
            const rows = res?.Data || [];
            if (rows.length === 0) return { rows: [], depPercentList: [], depPercentExist: false };

            const central = rows[0].Central;
            const depRes = await api.getItemDepPercentByCC(central, 'CC00');
            const maxPercent = depRes?.Data?.MaxPercent || 0;
            const centralStoreCC = depRes?.Data?.CentralStoreCC || null;
            return {
                rows,
                depPercentList: buildDepPercentList(maxPercent, centralStoreCC, central),
                depPercentExist: maxPercent > 0,
            };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load issue items'));
        }
    }
);

// Success format is "Submited,<IssueNo>".
export const submitOldStockIssue = createAsyncThunk(
    'oldStockIssue/submitOldStockIssue',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveOldItemsIssue(payload);
            const raw = res?.Data || '';
            const [status, refNo] = raw.split(',');
            if (status !== 'Submited') {
                return rejectWithValue(raw || 'Failed to submit old stock issue');
            }
            return { refNo };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to submit old stock issue'));
        }
    }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
    ccList: [],
    indentList: [],
    indentType: '',
    gridRows: [],
    depPercentList: [],
    depPercentExist: false,
    submitResult: null,
    loading: {
        ccList: false, indentList: false, indentType: false, grid: false, submit: false,
    },
    errors: {
        ccList: null, indentList: null, indentType: null, grid: null, submit: null,
    },
};

const oldStockIssueSlice = createSlice({
    name: 'oldStockIssue',
    initialState,
    reducers: {
        clearIndentList: (s) => { s.indentList = []; },
        clearIndentType: (s) => { s.indentType = ''; },
        clearGrid: (s) => { s.gridRows = []; s.depPercentList = []; s.depPercentExist = false; s.errors.grid = null; },
        clearSubmitResult: (s) => { s.submitResult = null; s.errors.submit = null; },
        resetOldStockIssue: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOldStockCCList.pending,   (s) => { s.loading.ccList = true; s.errors.ccList = null; })
            .addCase(fetchOldStockCCList.fulfilled, (s, a) => { s.loading.ccList = false; s.ccList = a.payload; })
            .addCase(fetchOldStockCCList.rejected,  (s, a) => { s.loading.ccList = false; s.errors.ccList = a.payload; });

        builder
            .addCase(fetchOldStockIndentList.pending,   (s) => { s.loading.indentList = true; s.errors.indentList = null; })
            .addCase(fetchOldStockIndentList.fulfilled, (s, a) => { s.loading.indentList = false; s.indentList = a.payload; })
            .addCase(fetchOldStockIndentList.rejected,  (s, a) => { s.loading.indentList = false; s.errors.indentList = a.payload; s.indentList = []; });

        builder
            .addCase(fetchIndentType.pending,   (s) => { s.loading.indentType = true; s.errors.indentType = null; })
            .addCase(fetchIndentType.fulfilled, (s, a) => { s.loading.indentType = false; s.indentType = a.payload; })
            .addCase(fetchIndentType.rejected,  (s, a) => { s.loading.indentType = false; s.errors.indentType = a.payload; });

        builder
            .addCase(fetchOldStockGrid.pending,   (s) => { s.loading.grid = true; s.errors.grid = null; })
            .addCase(fetchOldStockGrid.fulfilled, (s, a) => {
                s.loading.grid = false;
                s.gridRows = a.payload.rows;
                s.depPercentList = a.payload.depPercentList;
                s.depPercentExist = a.payload.depPercentExist;
            })
            .addCase(fetchOldStockGrid.rejected,  (s, a) => { s.loading.grid = false; s.errors.grid = a.payload; s.gridRows = []; });

        builder
            .addCase(submitOldStockIssue.pending,   (s) => { s.loading.submit = true; s.errors.submit = null; s.submitResult = null; })
            .addCase(submitOldStockIssue.fulfilled, (s, a) => { s.loading.submit = false; s.submitResult = a.payload; s.gridRows = []; })
            .addCase(submitOldStockIssue.rejected,  (s, a) => { s.loading.submit = false; s.errors.submit = a.payload; });
    },
});

export const {
    clearIndentList,
    clearIndentType,
    clearGrid,
    clearSubmitResult,
    resetOldStockIssue,
} = oldStockIssueSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectOSICCList          = (s) => s.oldStockIssueCreation.ccList;
export const selectOSIIndentList      = (s) => s.oldStockIssueCreation.indentList;
export const selectOSIIndentType      = (s) => s.oldStockIssueCreation.indentType;
export const selectOSIGridRows        = (s) => s.oldStockIssueCreation.gridRows;
export const selectOSIDepPercentList  = (s) => s.oldStockIssueCreation.depPercentList;
export const selectOSIDepPercentExist = (s) => s.oldStockIssueCreation.depPercentExist;
export const selectOSISubmitResult    = (s) => s.oldStockIssueCreation.submitResult;
export const selectOSILoading         = (s) => s.oldStockIssueCreation.loading;
export const selectOSIErrors          = (s) => s.oldStockIssueCreation.errors;

export default oldStockIssueSlice.reducer;
